import { queryOptions, skipToken } from "@tanstack/react-query";

import { MASTER_INVALIDATION_KEY } from "@/api/queryClient";
import {
  getMaintenance,
  getMaintenanceFollowupDuration,
  getMaintenancesByIds,
  getMaintenancesByWorkspaceId,
  getMaintenancesUpcomingVirtualByWorkspaceId,
  getMaintenanceTree,
  type ListMaintenancesByWorkspaceIdProps,
  type ListMaintenancesUpcomingVirtualByWorkspaceIdProps,
} from "@/api/resources/maintenances/api";
import { adaptMaintenanceExtended } from "@/api/resources/maintenances/selectors";
import i18n from "@/providers/i18n";
import { getEntityTreeLeveled } from "@/utils/entityTree";

const entity = i18n.t("maintenance");
const entityErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entity }),
};
const entities = i18n.t("maintenances");
const entitiesErrorMessage = {
  text: i18n.t("notify.error-reason.unexpected"),
  title: i18n.t("notify.get.error", { e: entities }),
};

export const maintenancesQueryKeys = {
  all: () => [...MASTER_INVALIDATION_KEY, "maintenances"],
  lists: () => [...maintenancesQueryKeys.all(), "list"],
  listByIds: (ids?: string[]) => [...maintenancesQueryKeys.lists(), "byIds", ids],
  listByWorkspaceId: (props: ListMaintenancesByWorkspaceIdProps) => [
    ...maintenancesQueryKeys.lists(),
    "byWorkspaceId",
    props,
  ],
  listUpcomingVirtualByWorkspaceId: (props: ListMaintenancesUpcomingVirtualByWorkspaceIdProps) => [
    ...maintenancesQueryKeys.lists(),
    "upcomingVirtualByWorkspaceId",
    props,
  ],
  // descendant trees (linked cases / maintenances) — a maintenance's title is also shown in case trees
  trees: () => [...maintenancesQueryKeys.all(), "tree"],
  tree: (id?: string) => [...maintenancesQueryKeys.trees(), id],
  followupDuration: (id?: string) => [...maintenancesQueryKeys.all(), "followupDuration", id],
  byId: (id?: string) => [...maintenancesQueryKeys.all(), "byId", id],
};

export const maintenancesQueries = {
  listByIds: (ids?: string[]) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.listByIds(ids)],
      queryFn: ids ? () => getMaintenancesByIds(ids) : skipToken,
      select: (data) => data.map(adaptMaintenanceExtended),
      meta: {
        errorMessage: entitiesErrorMessage,
      },
    }),
  listByWorkspaceId: (props: ListMaintenancesByWorkspaceIdProps) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.listByWorkspaceId(props)],
      queryFn: props.workspace_id ? () => getMaintenancesByWorkspaceId(props) : skipToken,
      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({
        count,
        data: data.map(adaptMaintenanceExtended),
      }),
    }),
  listUpcomingVirtualByWorkspaceId: (props: ListMaintenancesUpcomingVirtualByWorkspaceIdProps) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.listUpcomingVirtualByWorkspaceId(props)],
      queryFn: props.workspace_id ? () => getMaintenancesUpcomingVirtualByWorkspaceId(props) : skipToken,
      meta: {
        errorMessage: entitiesErrorMessage,
      },
      select: ({ data, count }) => ({
        count,
        data: data.map(adaptMaintenanceExtended),
      }),
    }),
  followupDuration: (id?: string) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.followupDuration(id)],
      queryFn: id ? () => getMaintenanceFollowupDuration(id) : skipToken,
      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  treeLeveled: (id?: string) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.tree(id)],
      queryFn: id ? () => getMaintenanceTree(id) : skipToken,
      select: getEntityTreeLeveled,
      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
  byId: (id?: string) =>
    queryOptions({
      queryKey: [...maintenancesQueryKeys.byId(id)],
      queryFn: id ? () => getMaintenance({ id }) : skipToken,
      select: adaptMaintenanceExtended,
      meta: {
        errorMessage: entityErrorMessage,
      },
    }),
};
